// Le FANTÔME DU RECORDMAN côté client (25/07, favorite 2 de l'audit de rétention).
//
// Deux objets, l'un pour enregistrer, l'autre pour rejouer :
//   - `GhostRecorder` mémorise l'entrée de CHAQUE tick de ta partie. Il se branche sur la seule
//     fonction que la boucle appelle une fois par tick (client/loop.ts : `tick(state, [input()])`),
//     donc il capte exactement ce que le moteur a reçu, ni plus ni moins.
//   - `GhostRun` rejoue les entrées d'un autre joueur sur UNE SECONDE partie du même niveau, à
//     raison d'un tick pour un tick. Comme le moteur est déterministe, sa course est au sous-pixel
//     près celle qu'il a faite.
//
// ⚠️ Le fantôme a sa PROPRE partie : ses moines, son or et ses trous n'existent que pour lui, et on
// ne dessine QUE son coureur. Il ne touche donc jamais à la partie du joueur (aucun risque de
// désynchronisation, aucun effet sur le hash d'état).
import { createGame, type GameState, type Runner } from '../shared/state'
import { decodeInputs, GHOST_MAX_TICKS } from '../shared/ghost'
import { tick } from '../shared/sim'
import type { LevelData, MonkSpeed, PlayerInput } from '../shared/types'

/** Tout ce qui, en plus des entrées, décide du déroulé d'une partie solo — donc tout ce que le
 *  fantôme doit rejouer À L'IDENTIQUE. La GRAINE en fait partie : `createGame` la reçoit et elle
 *  pilote le hasard des moines ; deux essais du même niveau n'ont pas la même (client/solo.ts fait
 *  `1000 * index + tries`). Un fantôme rejoué avec une autre graine croiserait d'autres moines et
 *  divergerait — c'est pour cela que la graine est enregistrée avec la course. */
export interface GhostSetup {
  seed: number
  dashEnabled: boolean
  monkSpeed?: MonkSpeed
  smartMonks?: boolean
  monkPowers?: boolean
}

/** Ce que le rendu a besoin de savoir pour dessiner le fantôme (positions interpolables). */
export interface GhostFrame { prev: Runner | null; cur: Runner | null }

/** Enregistreur : une entrée par tick, jusqu'au plafond. */
export class GhostRecorder {
  private readonly inputs: PlayerInput[] = []

  /** À appeler avec l'entrée EXACTE donnée au moteur pour ce tick. */
  push(i: PlayerInput): void {
    if (this.inputs.length < GHOST_MAX_TICKS) this.inputs.push(i)
  }

  /** Les entrées enregistrées (référence vivante ; à encoder en fin de partie). */
  get recorded(): readonly PlayerInput[] {
    return this.inputs
  }

  /** Vrai si la course tient dans le plafond : au-delà, on renonce plutôt que de tronquer (un
   *  fantôme coupé s'arrêterait au milieu de la carte, ce qui est pire que pas de fantôme). */
  get usable(): boolean {
    return this.inputs.length > 0 && this.inputs.length < GHOST_MAX_TICKS
  }
}

/**
 * Rejoueur : une partie parallèle nourrie par des entrées enregistrées. `step()` avance d'un tick
 * et met à jour la paire (prev, cur) que le rendu interpole. Quand les entrées sont épuisées ou que
 * sa partie est finie, le fantôme s'efface (cur = null) : il ne reste pas planté sur la carte.
 */
export class GhostRun {
  private readonly state: GameState
  private readonly inputs: PlayerInput[]
  private i = 0
  prev: Runner | null = null
  cur: Runner | null = null

  constructor(level: LevelData, ticks: string, setup: GhostSetup) {
    this.state = createGame(level, {
      mode: 'solo', seed: setup.seed, monksEnabled: true, players: 1,
      monkSpeed: setup.monkSpeed, smartMonks: setup.smartMonks, monkPowers: setup.monkPowers,
      dashEnabled: setup.dashEnabled,
    })
    this.inputs = decodeInputs(ticks)
    this.cur = this.state.runners[0] ? { ...this.state.runners[0] } : null
  }

  /** Vrai tant qu'il reste quelque chose à rejouer. */
  get running(): boolean {
    return this.i < this.inputs.length && !this.state.finished
  }

  /** Avance le fantôme d'UN tick, en phase avec la partie du joueur. */
  step(): void {
    if (!this.running) {
      this.prev = null
      this.cur = null
      return
    }
    // Copie plate : Runner n'est qu'un sac de nombres et de booléens (shared/state.ts), et le rendu
    // n'a besoin que de la position précédente pour interpoler.
    this.prev = this.state.runners[0] ? { ...this.state.runners[0] } : null
    tick(this.state, [this.inputs[this.i++]!])
    const r = this.state.runners[0]
    this.cur = r && r.alive ? r : null
  }

  /** L'image à dessiner ce frame (prev/cur), ou une paire vide si le fantôme a fini sa course. */
  frame(): GhostFrame {
    return { prev: this.prev, cur: this.cur }
  }
}
